import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import SubHeaderBannerShell from '../../components/layouts/SubHeader/SubHeaderBannerShell';
import ChatSidebarV2, { ChatMobileHistoryBarV2, ChatSidebarDrawerV2 } from './ChatSidebarV2';
import {
  CHAT_BANNER_EXTENSION_SX,
  CHAT_BODY_SX,
  CHAT_CONTENT_BANNER_LAYER_SX,
  CHAT_CONTENT_MAIN_SX,
  CHAT_CONTENT_MODERN_SX,
  CHAT_CONTENT_PANE_SX,
  CHAT_WRAPPER_SX,
} from './chatStyles';

export const CHAT_V2_SIDEBAR_COLLAPSED_KEY = 'alphasearch.chat.sidebarCollapsed';

function storedCollapsedPreference() {
  try {
    return localStorage.getItem(CHAT_V2_SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

export default function ChatLayoutV2({
  children,
  navigationData = {},
  sidebarProps = {},
  onNewChat,
  chatNavigationDisabled = false,
  activeView = 'chat',
  onOpenLibrary,
  chatChromeVersion = 'v2',
}) {
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [collapsed, setCollapsed] = useState(storedCollapsedPreference);
  const [mobileOpen, setMobileOpen] = useState(false);

  const setDesktopCollapsed = useCallback((next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(CHAT_V2_SIDEBAR_COLLAPSED_KEY, String(next));
    } catch (error) {
      // ignore
    }
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, activeView]);

  useEffect(() => {
    if (isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const handleNewChat = useCallback(() => {
    onNewChat?.();
    setMobileOpen(false);
  }, [onNewChat]);

  const handleOpenLibrary = useCallback(() => {
    onOpenLibrary?.();
    setMobileOpen(false);
  }, [onOpenLibrary]);

  const handleSelectSession = useCallback(async (sessionId) => {
    const opened = await sidebarProps.onSelectSession?.(sessionId);
    if (opened !== false) setMobileOpen(false);
    return opened;
  }, [sidebarProps]);

  const fullSidebarProps = {
    sessions: navigationData.sessions || [],
    loading: navigationData.sessionsLoading,
    error: navigationData.sessionsError,
    onRetrySessions: navigationData.refreshSessions,
    creditSummary: navigationData.creditSummary,
    creditLoading: navigationData.creditLoading,
    creditError: navigationData.creditError,
    subscriptionStatus: navigationData.subscriptionStatus,
    activeView,
    collapsed: isDesktop ? collapsed : false,
    onToggleCollapsed: () => setDesktopCollapsed(!collapsed),
    showRecentChats:
      !navigationData.sessionsLoading && (navigationData.sessions || []).length > 0,
    ...sidebarProps,
    onNewChat: handleNewChat,
    onOpenLibrary: handleOpenLibrary,
    onSelectSession: handleSelectSession,
  };

  const mobileTitle = activeView === 'library' ? 'Chat Library' : 'Chat';

  return (
    <MainLayout
      chatNavigationDisabled={chatNavigationDisabled}
      disableMobileBottomPadding
    >
      <Box sx={CHAT_WRAPPER_SX} data-testid="chat-shell-v2" data-chat-chrome={chatChromeVersion}>
        <Box sx={CHAT_BODY_SX}>
          {isDesktop ? (
            <ChatSidebarV2 {...fullSidebarProps} variant="persistent" />
          ) : null}
          <Box sx={CHAT_CONTENT_MODERN_SX}>
            {!isDesktop ? (
              <ChatMobileHistoryBarV2
                title={mobileTitle}
                onOpen={() => setMobileOpen(true)}
              />
            ) : null}
            <Box sx={CHAT_CONTENT_PANE_SX}>
              <Box sx={CHAT_CONTENT_BANNER_LAYER_SX}>
                <SubHeaderBannerShell testId="chat-content-banner" />
              </Box>
              <Box sx={CHAT_CONTENT_MAIN_SX}>
                <Box
                  aria-hidden
                  data-testid="chat-banner-gradient-extension"
                  sx={CHAT_BANNER_EXTENSION_SX}
                />
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {children}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {!isDesktop ? (
          <ChatSidebarDrawerV2
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sidebarProps={fullSidebarProps}
          />
        ) : null}
      </Box>
    </MainLayout>
  );
}
